import { OrderDetails as OrderDetailsComponent, ReturnsEvent } from '@lastbrand/post-order-web'
import { useTranslation } from 'next-i18next'
import React, { useCallback, useState } from 'react'

import { getB2BProductPathname } from '@src/components/product-card/product-card.helpers'
import { CONFIG_B2B_SELF_SERVICE_DOMAIN } from '@src/config/env'
import { STATSIG_EXPERIMENTS_BUCKETS } from '@src/config/statsig-experiments.config'
import { STATSIG_EXPERIMENTS, useStatsigExperiment } from '@src/experiments/statsig-integration'
import { useStatsigDynamicConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-config.hook'
import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
import {
  STATSIG_DYNAMIC_CONFIG_NAMES,
  STATSIG_GATE_NAMES,
  useStatsigGate,
} from '@src/experiments/statsig-integration/use-statsig-gate.hook'
import { useQuinceNprogressRouter } from '@src/modules/navigation'
import CreateReview from '@src/quince-components/src/atoms/reviews/createReview'
import {
  type ReviewSubmissionEntryContext,
  type TRecentPurchaseItem,
} from '@src/quince-components/src/atoms/reviews/reviewSubmission'
import { getProductLinkWithSelectedColor } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { TRACK_PAGE_TYPE } from '@src/quince-site-common/src/domains/PageTypes'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import { useIsSvdEnabled } from '@src/quince-site-common/src/hooks/use-is-svd-enabled'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { AppInstallAccounts } from '@src/quince-site-common/src/organisms/appLayout/molecules/footer/molecules/app-install/app-install-accounts'
import { useAppInstallAccountsPresenter } from '@src/quince-site-common/src/organisms/appLayout/molecules/footer/molecules/app-install/app-install.presenter'
import productBuyNowStateManager from '@src/quince-site-common/src/redux/globalStateManager/ProductBuyNowStateManager'
import { fetchAndEnrichProducts } from '@src/quince-site-common/src/services/productService/fetchAndEnrichProducts'
import { fetchStoreProgramById } from '@src/quince-site-common/src/services/storeProgramService'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { getPathParams } from '@src/quince-site-common/src/utils/getPathParam'
import { getURLStringFromInputString } from '@src/quince-site-common/src/utils/getURLStringFromInputString'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import AccountWrapper from '@src/quince-site-integrated/src/components/Account/account-wrapper'
import { AddressEditConfirmationModal } from '@src/quince-site-integrated/src/components/Account/address-edit-confirmation-modal/address-edit-confirmation-modal.component'
import EditAddressSideDrawer from '@src/quince-site-integrated/src/components/Account/editShippingAddressWrapper/EditShippingAddressWrapper'
import {
  getSelectedAttributesRecord,
  type VariantAttributesInfo,
} from '@src/quince-site-integrated/src/components/Account/smart-variant-attributes.util'
import { REVIEWS_SOURCES } from '@src/quince-site-pdp/src/organisms/productPageSections/productReviews/productReviews.constant'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { TelemetryEventDataMap, TelemetryEventNames } from '@src/telemetry/telemetry-events.types'
import { trackEvent } from '@src/telemetry/track'
import { isB2BSelfServiceOrder } from '@src/utils/b2b/b2b.utils'
import { getB2BPreviewDomain } from '@src/utils/navigate-to-b2b'

import { PostOrderProviderWrapper } from '../post-order-provider-wrapper'
import { PostOrderReviewModal } from '../review/post-order-review-modal'

import { LineItem } from './domains/line-item.type'
import { OrderDetailsType } from './domains/order-details.type'
import styles from './order-details.module.scss'

interface Props {
  orderId: string | number
  data: { accountPage: { addAddressForm: Record<string, unknown> } }
}

const ACTIVE_MODAL_EDIT_ADDRESS = 'activeModalEditAddress'
const ACTIVE_WRITE_REVIEW_MODAL = 'reviewModal'
const ADDRESS_EDIT_CONFIRMATION_MODAL = 'addressEditConfirmationModal'

interface SubscriptionAddressHandler {
  isSubscriptionOrder: boolean
  showConfirmationModal: () => void
}

const getReviewVariantId = (product: Product | null, lineItem: LineItem | null): string | undefined => {
  if (!lineItem) return undefined
  const matched = product?.variants?.find(
    variant => variant.svid === lineItem.variantId || variant.id === lineItem.variantId,
  )
  const resolved = matched?.id ?? lineItem.variantId
  return resolved != null ? String(resolved) : undefined
}

const getFulfillmentIdForLineItem = (order: OrderDetailsType | null, lineItem: LineItem | null): string | undefined => {
  if (!order || !lineItem) return undefined
  const fulfillment = order.fulfillments?.find(f => f.orderLineItems?.some(li => li.id === lineItem.id))
  return fulfillment?.id != null ? String(fulfillment.id) : undefined
}

interface WineOrderSummaryTextConfig {
  textEN?: string
  textFR?: string
}

const OrderDetails = ({ orderId, data }: Props) => {
  const { t } = useTranslation('common')
  const { shouldRender: showAppInstallBanner } = useAppInstallAccountsPresenter({ placement: 'order_details' })
  const { addAddressForm } = data.accountPage
  const [activeOrder, setActiveOrder] = useState<OrderDetailsType | null>(null)
  const [activeModal, setActiveModal] = useState<string | null>(null)
  const [product, setProduct] = useState<Product | null>(null)
  const [lineItem, setLineItem] = useState<LineItem | null>(null)
  const [subscriptionHandler, setSubscriptionHandler] = useState<SubscriptionAddressHandler | null>(null)
  const router = useQuinceNprogressRouter()
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const isWebView = getIsNativeViewClient()

  // Evaluate (and log exposure for) the review-flow experiment once the user opens a review — `product`
  // is set on the "Write a Review" click and persists through the submit → success-popup lifecycle — so
  // exposure isn't logged on every order-detail page view and the chosen arm stays stable while open.
  const hasOpenedReview = product != null
  const { experimentBucket: reviewFlowBucket, init: isReviewExperimentReady } = useStatsigExperiment(
    isWebView
      ? STATSIG_EXPERIMENTS.MY_ORDERS_REVIEWS_SUBMISSION_FLOW_APP
      : STATSIG_EXPERIMENTS.MY_ORDERS_REVIEWS_SUBMISSION_FLOW_WEB,
    hasOpenedReview,
  )
  const showNewReviewFlow = isReviewExperimentReady && reviewFlowBucket === STATSIG_EXPERIMENTS_BUCKETS.TEST_CAP

  // Set when the user picks another item to review from the thank-you step — restarts the
  // submission flow inside the same modal instead of routing to /reviews/submit.
  const [reviewEntryOverride, setReviewEntryOverride] = useState<ReviewSubmissionEntryContext | undefined>(undefined)

  const wineOrderSummaryTextConfig =
    useStatsigDynamicConfig<WineOrderSummaryTextConfig | undefined>('wineOrderSummaryText') ?? {}

  const showMultipleShipmentsInOrderDetails =
    useStatsigDynamicConfig<boolean>('showMultipleShipmentsInOrderDetails') ?? false

  const countriesWithoutProvinceCode = useStatsigDynamicConfig<string[]>('countriesWithoutProvinceCode')

  const subscriptionAddressUpdateMessage = useStatsigGate('subscription_address_update_message')
  const shouldShowInseamAttribute = useStatsigGate(STATSIG_GATE_NAMES.INSEAM_ATTRIBUTE_ENABLED)
  const showFlowerPrintedCard = useStatsigGate(STATSIG_GATE_NAMES.WEB_SHOW_FLOWER_PRINTED_CARD)
  const isSmartVariantEnabled = useIsSvdEnabled()
  const isVdaOrderChangeEnabled = useStatsigGate(STATSIG_GATE_NAMES.VDA_ORDER_CHANGE_ENABLED)
  const enableWriteReviewOnB2BPdp = useStatsigDynamicConfig<boolean>(
    STATSIG_DYNAMIC_CONFIG_NAMES.ENABLE_WRITE_REVIEW_ON_B2B_PDP,
  )
  const enableDeliveryInProgressStatus = useStatsigDynamicConfig<boolean>('enableDeliveryInProgressStatus') ?? false

  const {
    showItemizedTaxBreakdownInPriceSummary,
    showTotalExcludingVatInPriceSummary: showTotalExcludingVat,
    showDownloadInvoiceOrder,
    skipProvinceInAddress,
  } = useStatsigDynamicLocaleConfig()
  const statsigDynamicConfig = {
    wineOrderTextConfig: wineOrderSummaryTextConfig,
    showMultipleShipmentsInOrderDetails,
    showItemizedTaxBreakdownInPriceSummary,
    showTotalExcludingVat,
    showDownloadInvoiceOrder,
    subscriptionAddressUpdateMessage,
    skipProvinceInAddress,
    countriesWithoutProvinceCode,
    enableWriteReviewOnB2BPdp,
    enableDeliveryInProgressStatus,
  }

  const trackEventCall = <K extends TelemetryEventNames>(eventName: K, properties: TelemetryEventDataMap[K]) => {
    trackEvent(eventName, properties)
  }

  const trackReturnsEventCall = (eventName: ReturnsEvent, properties?: Record<string, unknown>) => {
    trackEvent(eventName as TelemetryEventNames, properties)
  }

  const handleEditAddress = (orderData: OrderDetailsType, subHandler?: SubscriptionAddressHandler) => {
    trackEvent(TELEMETRY_EVENT_NAMES.ORDER_DETAILS_ACTIONS, {
      event_type: 'order_details_actions',
      entity_type: 'edit_address_initiated',
      order_id: String(orderData.id),
    })
    setActiveOrder(orderData)
    setSubscriptionHandler(subHandler ?? null)
    setActiveModal(ACTIVE_MODAL_EDIT_ADDRESS)
  }

  const handleCancelOrder = (orderData: OrderDetailsType) => {
    trackEvent(TELEMETRY_EVENT_NAMES.ORDER_DETAILS_ACTIONS, {
      event_type: 'order_details_actions',
      entity_type: 'cancel_order_initiated',
      order_id: String(orderData.id),
    })
    // setActiveOrder(orderData)
    // setActiveModal(ACTIVE_MODAL_CANCEL)
    router.push(`/account/orders/${orderData.id}/cancel`)
  }

  const handleBuyItAgain = async (
    lineItem: {
      variantTitle?: string
      variantDetail?: { spid?: number } & VariantAttributesInfo
      productId?: number
      /** Internal variant id — seeds the popup's SVD selection (see `selectedCardVariantId` below). */
      variantId?: number
    },
    product: Product,
    order?: OrderDetailsType,
  ) => {
    setFindingMethod[FINDING_METHOD_AGGREGATE.MY_ORDERS]?.()
    // `variantTitle` carries no axis identity and the backend owns its ordering, so segment 0 is
    // not reliably the size. These two feed the product LINK (mweb + app webview) and the popup's
    // `selectedOptions`, so getting them wrong opens a variant the customer did not order.
    // Reads the ordered variant's own axes instead — available in both buckets, the order payload
    // ships `attributes` regardless of the client flag.
    const vdaAttributes =
      isSmartVariantEnabled || isVdaOrderChangeEnabled ? getSelectedAttributesRecord(lineItem.variantDetail) : undefined
    const [size = '', color = ''] = vdaAttributes
      ? [vdaAttributes.size ?? '', vdaAttributes.color ?? '']
      : (lineItem.variantTitle?.split(' / ') ?? [])

    // B2B Self-Serve: redirect to B2B PDP instead of opening modal
    const isB2B = isB2BSelfServiceOrder(order?.tags)
    const storeProgramId = order?.metaData?.storeProgramId
    if (isB2B && storeProgramId) {
      try {
        const [storeProgram, products] = await Promise.all([
          fetchStoreProgramById(storeProgramId),
          fetchAndEnrichProducts({
            ...(lineItem.variantDetail?.spid
              ? { spids: [String(lineItem.variantDetail.spid)] }
              : { internalProductIds: [String(lineItem.productId)] }),
            useLite: true,
          }),
        ])

        const slug = products[0]?.slug
        const program = storeProgram.storeProgramDetails?.slug
        const tenant = storeProgram.client?.code

        if (slug && program) {
          const b2bPath = getB2BProductPathname(slug, program)
          const params: Record<string, string> = {}
          if (color) params.color = getURLStringFromInputString(color)
          const qs = getPathParams(params, '')
          const fullPath = `${b2bPath}${qs}`

          const hostname = window.location.hostname
          const isLocalhost = hostname === 'localhost'
          if (isLocalhost) {
            window.location.href = fullPath
          } else {
            const b2bDomain =
              (tenant ? getB2BPreviewDomain(hostname, tenant) : undefined) ?? CONFIG_B2B_SELF_SERVICE_DOMAIN
            window.location.href = `${window.location.protocol}//${b2bDomain}${fullPath}`
          }
          return
        }
      } catch (error) {
        trackEvent(TELEMETRY_EVENT_NAMES.ORDER_DETAILS_ACTIONS, {
          event_type: 'order_details_actions',
          // eslint-disable-next-line i18next/no-literal-string
          entity_type: 'buy_it_again_b2b_fallback',
          order_id: order ? String(order.id) : undefined,
          product_id: lineItem.productId != null ? String(lineItem.productId) : undefined,
          variant_id: lineItem.variantDetail?.spid != null ? String(lineItem.variantDetail.spid) : undefined,
        })
        console.error('[B2B BuyAgain] Failed to resolve B2B product slug, falling back to D2C flow', error)
      }
    }

    try {
      const productLink = getProductLinkWithSelectedColor(product, [
        { name: ProductOptionName.Color, value: color },
        { name: ProductOptionName.Size, value: size },
      ])
      if (isWebView) {
        try {
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({
              // @i18n-ignore
              action: 'NAVIGATION',
              url: window.location.href,
              newURL: productLink.split('?')[0],
              nextURL: productLink,
            }),
          )
        } catch (error) {
          // open in webview if postmessage fails
          router.push(productLink)
          console.error('Error in sending message to native webview ', error)
        }
      } else if (isMobile) {
        router.push(productLink)
      } else {
        productBuyNowStateManager.showBuyNowPopUp({
          product,
          selectedOptions: [
            { name: ProductOptionName.Color, value: color },
            { name: ProductOptionName.Size, value: size },
          ],
          // The ordered variant, so the popup preselects what was actually bought.
          // `selectedOptions` above can only carry two axes (parsed out of the variant
          // title), so on a product with more — e.g. Colour / Age / Size — it names the
          // wrong axes and drops the rest, and the popup fell back to the first-available
          // cascade (STRFRNT-16863). The SVD selection host takes this id first and resolves
          // it to the variant's full per-axis coord map, however many axes the product has.
          //
          // INTERNAL variant id, not the spid-space `variantDetail.svid`: the host matches
          // `variant.id`, and the enrich adapter carries `id` through from the v2 payload
          // while `svid` holds the Shopify id. Passing svid here would silently miss and
          // fall back to the anchor seed — i.e. look exactly like the bug.
          //
          // Legacy (non-SVD) products ignore this and keep using `selectedOptions`.
          selectedCardVariantId: lineItem.variantId,
          // The QATC/PL popup resolves selection on the server and never reads the id above, so it
          // needs the axes themselves — all of them, not the colour/size pair `selectedOptions` holds.
          selectedAttributes: vdaAttributes,
        })
      }
    } catch (error) {
      trackEvent(TELEMETRY_EVENT_NAMES.ORDER_DETAILS_ACTIONS, {
        event_type: 'order_details_actions',
        entity_type: 'buy_it_again_failed',
        order_id: order ? String(order.id) : undefined,
        product_id: lineItem.productId != null ? String(lineItem.productId) : undefined,
        variant_id: lineItem.variantDetail?.spid != null ? String(lineItem.variantDetail.spid) : undefined,
      })
      console.error('Error Opening Buy Again Popup From Order Details', error)
    }
  }

  const handleWriteReview = (lineItem: LineItem, product: Product, order: OrderDetailsType) => {
    trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
      action: 'write_review_click',
      context: {
        orderId: order?.orderNumber ?? orderId,
        productId: product?.id,
        spid: product?.spid,
        lb_user_id: getlbUserId() ?? '',
      },
    })

    setLineItem(lineItem)
    setProduct(product)
    setActiveOrder(order)
    setReviewEntryOverride(undefined)
    setActiveModal(ACTIVE_WRITE_REVIEW_MODAL)
  }

  const resetWriteReviewContext = () => {
    setActiveOrder(null)
    setLineItem(null)
    setProduct(null)
    setReviewEntryOverride(undefined)
  }

  const handleReviewModalClose = (isReviewSubmitted?: boolean) => {
    setActiveModal(null)
    if (!isReviewSubmitted) {
      resetWriteReviewContext()
    }
  }

  // Thank-you "Done": close the modal and stay on the order page (never navigate away).
  const handleReviewDone = () => {
    setActiveModal(null)
    resetWriteReviewContext()
  }

  // Thank-you "Write a review" on a recent purchase: restart the flow for that item in-modal.
  const handleReviewAnotherItem = (item: TRecentPurchaseItem) => {
    setReviewEntryOverride({
      customerId: item.customerId != null ? String(item.customerId) : undefined,
      orderNumber: item.orderNumber != null ? String(item.orderNumber) : undefined,
      fulfillmentId: item.fulfillmentId != null ? String(item.fulfillmentId) : undefined,
      variantId: item.variantId != null ? String(item.variantId) : undefined,
      quinceProductId: item.quinceProductId != null ? String(item.quinceProductId) : undefined,
      author: item.author,
      source: REVIEWS_SOURCES.MY_ORDER,
    })
  }

  const handleModalClose = () => {
    setActiveOrder(null)
    setActiveModal(null)
    setLineItem(null)
    setProduct(null)
    setSubscriptionHandler(null)
  }

  const onErrorCallback = (err: string) => {
    captureException(err, { module: MODULES.POST_ORDER_WEB, feature: FEATURES.ORDER_DETAILS })
  }

  const pendingAddressSubmitRef = React.useRef<((updateCustomerSubscription: boolean) => Promise<void>) | null>(null)
  const addressUpdateSuccessRef = React.useRef(false)
  const refetchOrderDetailsRef = React.useRef<(() => Promise<void>) | null>(null)

  const registerRefetch = useCallback((refetch: () => Promise<void>) => {
    refetchOrderDetailsRef.current = refetch
  }, [])

  const handleAddressEditSuccess = () => {
    addressUpdateSuccessRef.current = true
    trackEvent(TELEMETRY_EVENT_NAMES.ORDER_DETAILS_ACTIONS, {
      event_type: 'order_details_actions',
      entity_type: 'edit_address_success',
      order_id: activeOrder ? String(activeOrder.id) : undefined,
    })
    if (!subscriptionHandler?.isSubscriptionOrder) {
      setActiveModal(ADDRESS_EDIT_CONFIRMATION_MODAL)
    }
  }

  const handleAddressConfirmationDone = async () => {
    try {
      await refetchOrderDetailsRef.current?.()
    } finally {
      handleModalClose()
    }
  }

  const handleBeforeAddressSubmit = (submitFn: (updateCustomerSubscription: boolean) => Promise<void>) => {
    if (subscriptionHandler?.isSubscriptionOrder) {
      pendingAddressSubmitRef.current = submitFn
      setActiveModal(null)
      subscriptionHandler.showConfirmationModal()
    } else {
      void submitFn(false)
    }
  }

  const handleSubscriptionAddressUpdate = async (_order: unknown, updateType: string) => {
    if (pendingAddressSubmitRef.current) {
      addressUpdateSuccessRef.current = false
      try {
        await pendingAddressSubmitRef.current(updateType === 'all_future_orders')
      } finally {
        pendingAddressSubmitRef.current = null
      }

      if (!addressUpdateSuccessRef.current) {
        throw new Error('Subscription address update failed')
      }
    }
  }

  return (
    <PostOrderProviderWrapper>
      {/* Auth redirect + session restore (reload / multi-pool), without account chrome — same idea as find-my-order. */}
      <AccountWrapper useRedirectionOnly>
        <div className={styles.container}>
          <OrderDetailsComponent
            orderId={Number(orderId)}
            // @ts-expect-error OrderDetailsComponent props type mismatch
            onEditAddress={handleEditAddress}
            // @ts-expect-error OrderDetailsComponent props type mismatch
            onCancelOrder={handleCancelOrder}
            // @ts-expect-error OrderDetailsComponent props type mismatch
            onBuyNow={(...args: Parameters<typeof handleBuyItAgain>) => {
              void handleBuyItAgain(...args)
            }}
            // @ts-expect-error OrderDetailsComponent props type mismatch
            onWriteReview={handleWriteReview}
            // @ts-expect-error OrderDetailsComponent props type mismatch
            trackEvents={trackEventCall}
            trackReturnsEvent={trackReturnsEventCall}
            onError={onErrorCallback}
            onSubscriptionAddressUpdate={(...args: Parameters<typeof handleSubscriptionAddressUpdate>) =>
              handleSubscriptionAddressUpdate(...args)
            }
            onRegisterRefetch={registerRefetch}
            statsigDynamicConfig={statsigDynamicConfig}
            isMobile={isMobile}
            shouldShowInseamAttribute={shouldShowInseamAttribute}
            isSmartVariantEnabled={isSmartVariantEnabled}
            showFlowerPrintedCard={showFlowerPrintedCard}
            isVdaOrderChangeEnabled={isVdaOrderChangeEnabled}
            appInstallBanner={
              showAppInstallBanner ? (
                <AppInstallAccounts
                  trackPageType={TRACK_PAGE_TYPE.ACCOUNT}
                  placement="order_details"
                  desktopConfig={{ title: t('get_the_app'), qrAlt: t('app_qr_alt') }}
                  mobileConfig={{
                    title: t('keep_track_orders'),
                    actionLabel: t('get_the_app'),
                  }}
                />
              ) : null
            }
          />
          {/* {activeModal === ACTIVE_MODAL_CANCEL && <CancelOrderDrawer closeModal={handleModalClose} orderId={orderId} />} */}
          {activeModal === ACTIVE_MODAL_EDIT_ADDRESS && (
            <EditAddressSideDrawer
              open
              closeModal={handleModalClose}
              activeOrder={activeOrder}
              addAddressForm={addAddressForm}
              onSuccess={handleAddressEditSuccess}
              onBeforeSubmit={subscriptionHandler?.isSubscriptionOrder ? handleBeforeAddressSubmit : undefined}
            />
          )}
          {product != null &&
            (showNewReviewFlow ? (
              activeModal === ACTIVE_WRITE_REVIEW_MODAL &&
              !!activeOrder?.orderNumber && (
                <PostOrderReviewModal
                  entryOverride={
                    reviewEntryOverride ?? {
                      customerId: activeOrder?.customerId != null ? String(activeOrder.customerId) : undefined,
                      orderNumber: activeOrder?.orderNumber != null ? String(activeOrder.orderNumber) : undefined,
                      fulfillmentId: getFulfillmentIdForLineItem(activeOrder, lineItem),
                      variantId: getReviewVariantId(product, lineItem),
                      quinceProductId: product?.id != null ? String(product.id) : undefined,
                      author: activeOrder?.customer?.firstName,
                      source: REVIEWS_SOURCES.MY_ORDER,
                    }
                  }
                  isWebView={isWebView}
                  onNativeAppHandOff={handleModalClose}
                  onClose={() => {
                    handleReviewModalClose(false)
                  }}
                  onDone={handleReviewDone}
                  onWriteReviewItem={handleReviewAnotherItem}
                />
              )
            ) : (
              // Legacy modal: stays mounted while a review is active (its `show` toggles visibility and
              // its own success popup persists after close) — preserves the pre-experiment behavior.
              <CreateReview
                show={activeModal === ACTIVE_WRITE_REVIEW_MODAL && !!activeOrder?.orderNumber}
                onCancel={handleReviewModalClose}
                onSuccessPopUpDismiss={resetWriteReviewContext}
                product={product}
                productProperties={getProductPropertiesForTracking(product, product.variants?.[0]?.svid?.toString())}
                reviewSource={REVIEWS_SOURCES.MY_ORDER}
                orderNumber={activeOrder?.orderNumber}
                svid={lineItem?.variantId}
                storeProgramId={activeOrder?.metaData?.storeProgramId ?? null}
                isB2BSelfService={isB2BSelfServiceOrder(activeOrder?.tags)}
              />
            ))}

          {activeModal === ADDRESS_EDIT_CONFIRMATION_MODAL && (
            <AddressEditConfirmationModal open onDone={handleAddressConfirmationDone} />
          )}
        </div>
      </AccountWrapper>
    </PostOrderProviderWrapper>
  )
}

export default OrderDetails
